// src/components/ResetPassword.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword, clearResetPasswordError } from '../contents/redux/actions/authActions';
import styles from './utilities/cssmodules/CreatePassword.module.css';
import { toast } from 'react-toastify';

import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import lccLogo from "../../src/images/lcc_logo.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { resetPasswordError, invalidResetPasswordTokenError } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    dispatch(clearResetPasswordError());
  }, [dispatch]);

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[^\w]/.test(password),
  };

  const passwordMeetsRules =
    passwordRules.minLength &&
    passwordRules.uppercase &&
    passwordRules.number &&
    passwordRules.symbol;

  const doPasswordsMatch =
    password && confirmPassword && password === confirmPassword;

  const displayError =
    localError || resetPasswordError || invalidResetPasswordTokenError;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setLocalError('Invalid or missing token. Please check your reset link.');
      return;
    }

    if (!password || !confirmPassword) {
      setLocalError('Please fill out both fields.');
      return;
    }

    if (!passwordMeetsRules) {
      setLocalError(
        'Password must have at least 8 characters, one uppercase letter, one number and one symbol.'
      );
      return;
    }

    if (!doPasswordsMatch) {
      setLocalError('Passwords do not match.');
      return;
    }

    setLocalError('');
    setLoading(true);

    try {
      await dispatch(resetPassword({ token, password, confirmPassword }, navigate));
      toast.success('Your password has been reset successfully.');
    } catch (error) {
      // Handled in action
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.create_password_wrapper}>
        <h2>Reset Your Password</h2>
        <p className={styles.error_message}>
          Invalid or missing token. Please check your reset link.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.create_password_wrapper}>
      <div className={styles.action_component_header}>
        <div className={styles.action_component_header_logo}>
          <img src={lccLogo} alt="lcc logo" className={styles.logo_1} />
        </div>
        <div className={styles.action_component_header_text}>
          Liberia Chamber of Commerce
        </div>
      </div>

      <p className={styles.description}>
        Please create a new password for your account.  
        Make sure your password meets the security requirements below.
      </p>
      {displayError && <p className={styles.error_message}>{displayError}</p>}

      <form onSubmit={handleSubmit} className={styles.create_password_form}>
        {/* Password field */}
        <div className={styles.field_group}>
          <label htmlFor="password">New password</label>
          <div className={styles.password_input_wrapper}>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              className={styles.eye_button}
            >
              {showPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
            </span>
          </div>
        </div>

        {/* Confirm password */}
        <div className={styles.field_group}>
          <label htmlFor="confirmPassword">Confirm new password</label>
          <div className={styles.password_input_wrapper}>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={styles.eye_button}
            >
              {showConfirmPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
            </span>
          </div>
          {confirmPassword && !doPasswordsMatch && (
            <p className={styles.error_message}>Passwords do not match.</p>
          )}
        </div>

        {/* Validation feedback */}
        <div className={styles.validation_feedback}>
          <h4>Your password must contain:</h4>
          <p className={passwordRules.minLength ? styles.valid_rule : styles.invalid_rule}>
            At least 8 characters
          </p>
          <p className={passwordRules.uppercase ? styles.valid_rule : styles.invalid_rule}>
            At least one uppercase letter
          </p>
          <p className={passwordRules.number ? styles.valid_rule : styles.invalid_rule}>
            At least one number
          </p>
          <p className={passwordRules.symbol ? styles.valid_rule : styles.invalid_rule}>
            At least one symbol (!@#$%^&amp;*/, etc.)
          </p>
        </div>

        {/* Submit */}
        {passwordMeetsRules && doPasswordsMatch && (
          <button
            type="submit"
            disabled={loading}
            className={styles.submit_button}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        )}
      </form>
    </div>
  );
};

export default ResetPassword;
